import { ILeadIntentOption } from '@bridebook/models/source/models/Suppliers/Leads.types';

export type EnquiryIntentType = {
  [key in ILeadIntentOption]: boolean;
};

export interface EnquiryDetailedIntent {
  id: keyof EnquiryIntentType;
  label: string;
  checked: boolean;
}

export type EnquiryIntents = {
  [key in keyof EnquiryIntentType]: boolean;
};

export type EnquiryDetailedIntents = {
  [key in keyof EnquiryIntentType]: EnquiryDetailedIntent;
};

export const EnquiryIntent: EnquiryIntentType = {
  brochure: false,
  pricing: false,
  availability: false,
  showround: false,
  moreAvailability: false,
  other: false,
};

export const EnquiryIntentCheckboxes: EnquiryIntents = {
  ...EnquiryIntent,
  brochure: true,
};

/**
 * LIVE-27666 message-section pills — subset of {@link ILeadIntentOption} stored on `ILead.intent`.
 * Venue: general (brochure), pricing, availability, viewings (showround).
 */
export const LIVE_27666_VENUE_INTENT_OPTIONS = [
  ILeadIntentOption.brochure,
  ILeadIntentOption.pricing,
  ILeadIntentOption.availability,
  ILeadIntentOption.showround,
] as const;

/** Supplier: general (brochure), pricing, availability. */
export const LIVE_27666_SUPPLIER_INTENT_OPTIONS = [
  ILeadIntentOption.brochure,
  ILeadIntentOption.pricing,
  ILeadIntentOption.availability,
] as const;

export const getLIVE_27666IntentOptions = (isVenue: boolean): readonly ILeadIntentOption[] =>
  isVenue ? LIVE_27666_VENUE_INTENT_OPTIONS : LIVE_27666_SUPPLIER_INTENT_OPTIONS;

/** Maps toggle-group values to the full `intent` form object (non-pill keys stay false). */
export const buildLIVE_27666IntentFromToggleValues = (
  pillKeys: readonly ILeadIntentOption[],
  values: readonly string[],
): EnquiryIntents => {
  const selected = new Set(values);
  const pillKeySet = new Set(pillKeys);

  return Object.values(ILeadIntentOption).reduce<EnquiryIntents>(
    (acc, key) => ({
      ...acc,
      [key]: pillKeySet.has(key) && selected.has(key),
    }),
    { ...EnquiryIntent },
  );
};

export const getLIVE_27666TypeDefaultIntent = (isVenue: boolean): ILeadIntentOption =>
  isVenue ? ILeadIntentOption.brochure : ILeadIntentOption.pricing;

/** Selected LIVE-27666 pill keys from a full intent object, in pill display order. */
export const getSelectedLIVE_27666PillKeys = (
  isVenue: boolean,
  intent: Partial<EnquiryIntents>,
): ILeadIntentOption[] => getLIVE_27666IntentOptions(isVenue).filter((key) => intent[key]);

/**
 * Initial intent for LIVE-27666, by priority:
 *  1. Intent-setting CTA — `defaultIntent` is a visible pill that differs from the type default →
 *     force it, ignoring cache and defaults (`forcedByCTA: true`).
 *  2. Cache exists → use cached pills only (no union with default).
 *  3. Otherwise → type default (brochure for venues, pricing for suppliers).
 *
 * A generic open carries the type default as `defaultIntent`, so it reads as "no CTA opinion".
 */
export const resolveLIVE_27666_InitialIntent = ({
  isVenue,
  cachedIntent,
  defaultIntent,
}: {
  isVenue: boolean;
  cachedIntent?: Partial<EnquiryIntents>;
  defaultIntent?: keyof EnquiryIntents;
}): { intent: EnquiryIntents; forcedByCTA: boolean } => {
  const pillKeys = getLIVE_27666IntentOptions(isVenue);
  const typeDefault = getLIVE_27666TypeDefaultIntent(isVenue);

  const isForcingCTA =
    !!defaultIntent &&
    pillKeys.includes(defaultIntent as ILeadIntentOption) &&
    defaultIntent !== typeDefault;
  if (isForcingCTA) {
    return {
      intent: buildLIVE_27666IntentFromToggleValues(pillKeys, [defaultIntent as string]),
      forcedByCTA: true,
    };
  }

  const cachedPills = getSelectedLIVE_27666PillKeys(isVenue, cachedIntent ?? {});
  if (cachedPills.length > 0) {
    return {
      intent: buildLIVE_27666IntentFromToggleValues(pillKeys, cachedPills),
      forcedByCTA: false,
    };
  }

  return {
    intent: buildLIVE_27666IntentFromToggleValues(pillKeys, [typeDefault]),
    forcedByCTA: false,
  };
};
